import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];
// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🛒",
    title: "Custom E-Commerce Platform",
    desc: "Fully bespoke e-commerce storefronts built from scratch — product catalogues, advanced filtering, multi-currency checkout, abandoned cart recovery, wishlist, product reviews, and a merchant admin panel engineered for conversion.",
    tags: ["Custom Build", "Multi-Currency", "Cart Recovery", "SEO-Optimised"],
    color: "#E11D48",
  },
  {
    icon: "📱",
    title: "Commerce Mobile App",
    desc: "Native iOS and Android shopping apps with personalised feeds, push notification campaigns, one-tap reorder, AR try-on, loyalty points, in-app chat support, and biometric checkout — built for repeat purchase.",
    tags: ["iOS & Android", "AR Try-On", "Push Campaigns", "Loyalty Points"],
    color: "#7C3AED",
  },
  {
    icon: "🤖",
    title: "AI Personalisation & Recommendations",
    desc: "Collaborative filtering and deep learning recommendation engines that show the right product to the right customer at the right moment — increasing average order value by 28% and repeat purchase rate by 35% on average.",
    tags: ["Collaborative Filtering", "GPT-4", "Personalisation", "A/B Testing"],
    color: "#2563EB",
  },
  {
    icon: "🏪",
    title: "Multi-Vendor Marketplace",
    desc: "Scalable multi-seller marketplace platforms — vendor onboarding, product approval workflows, commission management, seller dashboards, dispute resolution, and a unified buyer experience across thousands of sellers.",
    tags: ["Multi-Vendor", "Commission Mgmt", "Seller Dashboard", "Dispute Flow"],
    color: "#059669",
  },
  {
    icon: "📦",
    title: "Order & Inventory Management",
    desc: "Real-time inventory tracking across warehouses and stores, automated reorder triggers, multi-location stock allocation, purchase order management, returns processing, and dead stock analytics.",
    tags: ["Multi-Location", "Auto-Reorder", "Returns Mgmt", "Dead Stock"],
    color: "#D97706",
  },
  {
    icon: "💳",
    title: "Payment Gateway & Checkout Optimisation",
    desc: "PCI-DSS-compliant multi-payment integration — UPI, cards, wallets, BNPL, EMI, COD — with one-page checkout, address autofill, payment failure retry logic, and checkout A/B testing that lifts conversion by 18%+.",
    tags: ["Razorpay", "Stripe", "BNPL / EMI", "UPI"],
    color: "#0891B2",
  },
  {
    icon: "📊",
    title: "Retail Analytics & Business Intelligence",
    desc: "Unified commerce dashboards — sales by channel, SKU-level profitability, cohort retention, customer lifetime value, funnel drop-off analysis, marketing attribution, and real-time revenue monitoring.",
    tags: ["CLV", "Cohort Analysis", "Attribution", "Real-Time BI"],
    color: "#DC2626",
  },
  {
    icon: "🔗",
    title: "Omnichannel & Marketplace Integrations",
    desc: "Sync inventory and orders across Amazon, Flipkart, Meesho, Myntra, Nykaa, ONDC, your own store, and offline POS — centralised catalogue management with rule-based pricing per channel.",
    tags: ["Amazon", "Flipkart", "ONDC", "POS Sync"],
    color: "#6366F1",
  },
];

const TECH_STACK = [
  {
    category: "Storefront",
    items: [
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000000" },
      { name: "React.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", color: "#02569B" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", color: "#42B883" },
    ],
  },
  {
    category: "Backend & AI",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
      { name: "TensorFlow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg", color: "#FF6F00" },
      { name: "Apache Kafka", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apachekafka/apachekafka-original.svg", color: "#231F20" },
      { name: "Elasticsearch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/elasticsearch/elasticsearch-original.svg", color: "#005571" },
    ],
  },
  {
    category: "Payments & Search",
    items: [
      { name: "Razorpay", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#3395FF" },
      { name: "Stripe", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#635BFF" },
      { name: "Algolia", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#003DFF" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
      { name: "Cloudinary", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#3448C5" },
      { name: "Twilio", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#F22F46" },
    ],
  },
  {
    category: "Cloud & Database",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "GCP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Commerce Discovery & Conversion Audit", desc: "Deep-dive workshops with your merchandising, marketing, and operations teams — mapping customer journeys, drop-off points, competitor benchmarks, integration requirements, and revenue KPIs before any design begins.", color: "#E11D48" },
  { n: "02", icon: "🎨", title: "UX Research & Conversion-First Design", desc: "User research with real shoppers from your target audience. Mobile-first, performance-optimised storefronts — product page, search, and checkout UX designed to maximise add-to-cart and purchase completion rates.", color: "#7C3AED" },
  { n: "03", icon: "⚙️", title: "Agile Development with Commerce QA", desc: "2-week sprints with dedicated e-commerce QA — checkout flow testing across 15+ payment methods, cart edge case validation, inventory accuracy checks, and load testing at 10× Black Friday traffic volumes.", color: "#2563EB" },
  { n: "04", icon: "🔗", title: "Payments, Logistics & Marketplace Integrations", desc: "Payment gateway connections (Razorpay, Stripe, PayU), logistics API integrations (Delhivery, Shiprocket, Ekart), marketplace channel sync (Amazon, Flipkart, ONDC), ERP/accounting connections, and WhatsApp commerce APIs.", color: "#D97706" },
  { n: "05", icon: "🧪", title: "Performance Audit, SEO & UAT", desc: "Core Web Vitals optimisation (target LCP < 2.5s), structured data implementation for product schema, technical SEO audit, cross-device QA across 20+ combinations, and a structured UAT with real shoppers before launch.", color: "#DC2626" },
  { n: "06", icon: "🚀", title: "Launch, Growth & Continuous Optimisation", desc: "Phased launch with data migration, team training, 24/7 SLA support, monthly conversion rate optimisation reports, A/B testing roadmap, marketing tech integrations, and continuous feature iteration driven by shopper analytics.", color: "#059669" },
];

const CASE_STUDIES = [
  {
    title: "StyleNest — Fashion D2C Platform",
    industry: "Fashion Retail",
    desc: "Built a full-stack D2C fashion platform for a premium Indian apparel brand — AI outfit recommendations, AR virtual try-on, size-fit prediction, WhatsApp order updates, and a loyalty programme. GMV grew 3.4× in 6 months with a 4.6★ app rating.",
    img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop&auto=format",
    tags: ["Next.js", "Flutter", "Razorpay", "GPT-4"],
    metrics: [{ val: "3.4×", lbl: "GMV Growth" }, { val: "4.6★", lbl: "App Store Rating" }, { val: "38%", lbl: "Repeat Purchase Rate" }],
    color: "#E11D48",
  },
  {
    title: "GrocerHub — Hyperlocal Grocery Platform",
    industry: "Grocery & FMCG",
    desc: "A 10-minute hyperlocal grocery delivery platform for a 200-store retail chain — dark store management, real-time inventory sync, AI slot allocation, driver dispatch, and a shopper app handling 25,000+ daily orders across 8 cities.",
    img: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=600&h=400&fit=crop&auto=format",
    tags: ["React Native", "Node.js", "Google Maps API", "Kafka"],
    metrics: [{ val: "25K+", lbl: "Daily Orders" }, { val: "8", lbl: "Cities Live" }, { val: "11min", lbl: "Avg Delivery Time" }],
    color: "#059669",
  },
  {
    title: "CraftBazaar — Multi-Vendor Marketplace",
    industry: "Handicraft Marketplace",
    desc: "A pan-India multi-vendor marketplace for 8,000+ independent artisans — seller onboarding with Aadhaar KYC, automated commission payouts via Razorpay Route, ONDC integration, and an AI recommendation engine. ₹24Cr GMV in year one.",
    img: "https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=600&h=400&fit=crop&auto=format",
    tags: ["Next.js", "Razorpay Route", "ONDC", "Python"],
    metrics: [{ val: "₹24Cr", lbl: "Year-1 GMV" }, { val: "8,000+", lbl: "Active Sellers" }, { val: "ONDC", lbl: "Network Ready" }],
    color: "#7C3AED",
  },
];

const WHY_US = [
  { icon: "🛒", title: "Deep E-Commerce Domain Expertise", desc: "Our team includes ex-Flipkart, Myntra, and Nykaa engineers, CRO specialists, and retail merchandisers who understand shopper psychology, catalogue architecture, and the operational complexity of high-volume retail — not just developers.", color: "#E11D48" },
  { icon: "⚡", title: "Built for Black Friday Traffic", desc: "Every e-commerce platform we build is load-tested at 10× peak traffic before launch. Auto-scaling Kubernetes clusters, Redis caching for product pages, CDN-accelerated assets, and database connection pooling — zero downtime during sale events.", color: "#7C3AED" },
  { icon: "🤖", title: "AI That Lifts Revenue, Not Just CTR", desc: "Our recommendation engines, personalised search, and dynamic pricing models are trained on real purchase data, validated against A/B test cohorts, and measured against revenue impact — not just click-through rates.", color: "#2563EB" },
  { icon: "🔗", title: "Every Indian Channel Connected", desc: "Amazon, Flipkart, Meesho, Myntra, Nykaa, Ajio, ONDC, Jiomart — we have production integrations with all major Indian marketplaces, including ONDC network participants. Plus WhatsApp Commerce, Instagram Shopping, and Google Shopping.", color: "#D97706" },
  { icon: "📊", title: "Conversion Rate Optimisation Built In", desc: "CRO is not an afterthought — checkout flow, product page layout, search relevance, and promotional mechanics are A/B tested from launch. Average 18–28% checkout conversion improvement across our deployments.", color: "#DC2626" },
  { icon: "📱", title: "Mobile Commerce That Converts", desc: "75% of Indian e-commerce happens on mobile. Our apps and PWAs are designed for thumb navigation, low-bandwidth product image loading, UPI-first checkout, and sub-3s add-to-cart on mid-range Android devices.", color: "#059669" },
];

const FAQS = [
  {
    q: "Should we build a custom e-commerce platform or use Shopify / WooCommerce?",
    a: "It depends on your scale, differentiation needs, and long-term roadmap. Shopify or WooCommerce is the right choice if you need a standard storefront fast, have a limited budget, and don't require deep custom functionality. A custom platform is the right choice if you process 500+ orders per day and need performance optimisation, if you have a marketplace or multi-vendor model that platforms don't support well, if you need deep integration with your own ERP/WMS/CRM, or if your product category requires a unique shopping experience (e.g., jewellery with AR try-on, furniture with room visualiser). We'll recommend the right approach — including hybrid models — after a free discovery call.",
  },
  {
    q: "Can you integrate our store with Amazon, Flipkart, and ONDC simultaneously?",
    a: "Yes — multichannel commerce integration is one of our core specialisations. We build centralised product catalogue management systems where you update a product once and it syncs to all channels: your own store, Amazon (via SP-API), Flipkart (via Seller Hub API), Meesho, Myntra, Nykaa, and ONDC (as a Seller App or Buyer App). Inventory is allocated per channel with buffer rules, and orders flow into a single OMS regardless of source channel. ONDC integration includes network registration, cataloguing as per specification, and buyer/seller app compliance.",
  },
  {
    q: "How do you handle payment failures and cart abandonment?",
    a: "Payment failure recovery is a first-class feature in our checkout architecture — not an afterthought. We implement smart retry logic that detects the failure type (insufficient funds, network timeout, bank decline) and presents the most relevant alternative payment method. Abandoned cart recovery uses a tiered approach: WhatsApp message at 30 minutes, email at 2 hours, push notification at 24 hours, with personalised discount triggers for high-value carts. Average cart recovery rate across our deployments is 12–18%. We A/B test every element of this flow from launch.",
  },
  {
    q: "What does your e-commerce platform look like on Core Web Vitals?",
    a: "We engineer for Core Web Vitals from the architecture level, not as a post-launch optimisation. Typical outcomes: LCP (Largest Contentful Paint) < 2.0s using Next.js SSR/ISR, product image CDN with WebP format and lazy loading, and above-the-fold pre-rendering. CLS (Cumulative Layout Shift) < 0.05 using reserved dimensions for all images and ads. INP (Interaction to Next Paint) < 150ms using client-side routing and prefetching. Google PageSpeed scores typically 90+ on mobile. This directly impacts SEO rankings for product category and brand pages.",
  },
  {
    q: "Can you build a loyalty and rewards programme into the platform?",
    a: "Yes — loyalty mechanics are a standard module in our commerce platform builds. We implement points-based programmes (earn on purchase, reviews, referrals; redeem at checkout), tiered membership (Bronze/Silver/Gold with escalating benefits), birthday bonuses, category-specific multipliers, and gamified milestones. For D2C brands, we also build referral programmes with unique referral links, WhatsApp sharing, and automated reward crediting. Loyalty data feeds back into the recommendation engine — loyal customers see personalised offers at their tier-appropriate discount ceiling.",
  },
  {
    q: "What post-launch support do you offer for e-commerce platforms?",
    a: "E-commerce platforms never sleep — a checkout bug at 11 PM on a sale day can cost lakhs in lost revenue. We offer Standard (business hours, 4hr critical SLA), Commerce (extended hours, 1hr critical SLA for checkout/payment issues), and Mission-Critical (24/7, 15-min SLA with dedicated on-call engineers for flash sales and peak events) support tiers. All tiers include 24/7 uptime monitoring, payment success rate alerting, monthly CRO reports, and quarterly platform health reviews. We also manage payment gateway API updates, marketplace integration changes, and security patches proactively.",
  },
];

const STATS = [
  { val: "200+", lbl: "E-Commerce Projects Delivered", icon: "🛒" },
  { val: "₹800Cr+", lbl: "Annual GMV Processed", icon: "💰" },
  { val: "2.4×", lbl: "Avg Conversion Lift", icon: "📈" },
  { val: "99.9%", lbl: "Checkout Uptime SLA", icon: "⚡" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.93)",
      backdropFilter: "blur(20px)",
      borderBottom: scrolled ? "1px solid #FECDD3" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 28px rgba(225,29,72,.08)" : "none",
      transition: "all .3s ease",
    }}>
      {/* Top bar */}
      <div style={{
        background: "linear-gradient(90deg,#18181B,#E11D48,#7C3AED)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .9 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>

      {/* Main bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>

        {/* Desktop links */}
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => (
            <div key={link.label} style={{ position: "relative" }}
              onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {link.link ? (
                <a href={link.link} style={{
                  background: currentPath === link.link ? "#FFF1F2" : "none",
                  border: currentPath === link.link ? "1px solid #FECDD3" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: currentPath === link.link ? "#E11D48" : "#374151",
                  display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none",
                  transition: "all .2s",
                }}>{link.label}</a>
              ) : (
                <button style={{
                  background: link.label === "Industries" ? "#FFF1F2" : "none",
                  border: link.label === "Industries" ? "1px solid #FECDD3" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: link.label === "Industries" ? "#E11D48" : activeDropdown === link.label ? "#E11D48" : "#374151",
                  display: "flex", alignItems: "center", gap: 4, transition: "all .2s",
                  fontFamily: "'Sora',sans-serif", borderRadius: 8,
                }}>
                  {link.label}
                  {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>}
                </button>
              )}
              {link.dropdown && activeDropdown === link.label && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, zIndex: 200,
                  background: "#fff", border: "1px solid #FECDD3", borderRadius: 16,
                  boxShadow: "0 20px 60px rgba(225,29,72,.1)", minWidth: 230,
                  padding: "8px 0", animation: "fadeIn .2s ease",
                }}>
                  {link.dropdown.map(item => (
                    <a key={item.name} href={item.link} style={{
                      display: "block", padding: "10px 20px", fontSize: 13.5,
                      color: item.name === "Retail & E-Commerce" ? "#E11D48" : "#374151",
                      background: item.name === "Retail & E-Commerce" ? "#FFF1F2" : "none",
                      fontWeight: item.name === "Retail & E-Commerce" ? 700 : 500,
                      textDecoration: "none", transition: "all .15s",
                      fontFamily: "'Sora',sans-serif",
                    }}
                      onMouseEnter={e => { e.target.style.background = "#FFF1F2"; e.target.style.color = "#E11D48"; }}
                      onMouseLeave={e => {
                        e.target.style.background = item.name === "Retail & E-Commerce" ? "#FFF1F2" : "none";
                        e.target.style.color = item.name === "Retail & E-Commerce" ? "#E11D48" : "#374151";
                      }}
                    >{item.name}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#18181B,#E11D48)", color: "#fff",
            padding: "10px 22px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 16px rgba(225,29,72,.3)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(225,29,72,.42)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(225,29,72,.3)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: mobileOpen ? "#FFF1F2" : "none",
            border: "1px solid #FECDD3", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
            transition: "all .2s",
          }}>{mobileOpen ? "✕" : "☰"}</button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #FECDD3",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          boxShadow: "0 16px 48px rgba(0,0,0,.1)",
        }}>
          <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #FFF1F2" }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          <div style={{ padding: "8px 0" }}>
            {NAV_LINKS.map(link => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    <button onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)} style={{
                      width: "100%", textAlign: "left", padding: "13px 24px",
                      background: mobileDropdown === link.label ? "#FFF1F2" : "none",
                      border: "none", borderBottom: "1px solid #FFF1F2",
                      color: mobileDropdown === link.label ? "#E11D48" : "#374151",
                      fontWeight: 600, fontSize: 15, fontFamily: "'Sora',sans-serif",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      {link.label}
                      <span style={{ fontSize: 11, color: "#9CA3AF", transition: "transform .2s", transform: mobileDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>
                    </button>
                    {mobileDropdown === link.label && (
                      <div style={{ background: "#FFF5F6", borderBottom: "1px solid #FFF1F2" }}>
                        {link.dropdown.map(item => (
                          <a key={item.name} href={item.link} onClick={() => setMobileOpen(false)} style={{
                            display: "block", padding: "11px 24px 11px 40px", fontSize: 14,
                            color: item.name === "Retail & E-Commerce" ? "#E11D48" : "#4B5563",
                            background: item.name === "Retail & E-Commerce" ? "#FFF1F2" : "none",
                            fontWeight: item.name === "Retail & E-Commerce" ? 700 : 500,
                            textDecoration: "none", borderBottom: "1px solid #FFF1F2",
                            fontFamily: "'Sora',sans-serif",
                          }}>→ {item.name}</a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a href={link.link || "#"} onClick={() => setMobileOpen(false)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "13px 24px",
                    color: currentPath === link.link ? "#E11D48" : "#374151",
                    background: currentPath === link.link ? "#FFF1F2" : "none",
                    fontWeight: 600, fontSize: 15, textDecoration: "none",
                    borderBottom: "1px solid #FFF1F2", fontFamily: "'Sora',sans-serif",
                  }}>
                    {link.label}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>›</span>
                  </a>
                )}
              </div>
            ))}
          </div>
          <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <a href="#contact" onClick={() => setMobileOpen(false)} style={{
              background: "linear-gradient(135deg,#18181B,#E11D48)", color: "#fff",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", boxShadow: "0 6px 20px rgba(225,29,72,.3)",
            }}>🛒 Get Free Consultation</a>
            <a href="tel:+919646206032" style={{
              background: "#FFF1F2", color: "#18181B",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", border: "1px solid #FECDD3",
            }}>📞 Call: +91 96462 06032</a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["₹800Cr+ Annual GMV Processed", "2.4× Avg Conversion Lift", "99.9% Checkout Uptime SLA", "200+ E-Commerce Projects Delivered"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 100,
      background: "linear-gradient(155deg,#FFF1F2 0%,#F5F3FF 35%,#EFF6FF 65%,#F8FAFC 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(225,29,72,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(225,29,72,.04) 1px,transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      {/* Curved blobs */}
      <div style={{ position: "absolute", top: -180, right: -120, width: 700, height: 700, borderRadius: "61% 39% 70% 30%/30% 50% 50% 70%", background: "radial-gradient(circle,rgba(225,29,72,.1),rgba(124,58,237,.05) 50%,transparent 75%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -120, left: -80, width: 560, height: 560, borderRadius: "39% 61% 30% 70%/70% 30% 70% 30%", background: "radial-gradient(circle,rgba(37,99,235,.07),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "30%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(5,150,105,.05),transparent 70%)", pointerEvents: "none" }} />

      {/* Decorative retail icons */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[
          { x: "6%", y: "18%", sym: "🛒" },
          { x: "91%", y: "14%", sym: "🏷️" },
          { x: "14%", y: "78%", sym: "💳" },
          { x: "88%", y: "72%", sym: "📦" },
          { x: "52%", y: "90%", sym: "🎁" },
        ].map((item, i) => (
          <div key={i} style={{
            position: "absolute", left: item.x, top: item.y,
            fontSize: 20, opacity: 0.08 + i * 0.02,
            animation: `ecPulse ${3 + i}s ease-in-out infinite ${i * 0.6}s`,
          }}>{item.sym}</div>
        ))}
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#9CA3AF" }}>
          <a href="/" style={{ color: "#E11D48", textDecoration: "none", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Home</a>
          <span>›</span>
          <span style={{ fontFamily: "'Sora',sans-serif" }}>Industries</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Retail & E-Commerce</span>
        </div>

        <div className="ec-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#FFF1F2", border: "1px solid #FECDD3", borderRadius: 99, padding: "6px 18px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#E11D48", display: "inline-block", animation: "ecPulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#18181B", fontFamily: "'Sora',sans-serif" }}>🛒 India's Trusted Retail & E-Commerce Technology Partner</span>
            </div>

            <h1 style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(36px,4.8vw,64px)",
              fontWeight: 900, color: "#18181B",
              lineHeight: 1.1, marginBottom: 20,
            }}>
              Commerce Software<br />
              <span style={{ background: "linear-gradient(135deg,#E11D48,#7C3AED,#2563EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Built to Convert,
              </span><br />
              Engineered to Scale.
            </h1>

            <p style={{ fontSize: 17, color: "#4B5563", lineHeight: 1.85, maxWidth: 570, marginBottom: 36, fontFamily: "'Sora',sans-serif", fontWeight: 400 }}>
              We build high-conversion e-commerce platforms, AI-powered recommendation engines, multi-vendor marketplaces, omnichannel retail systems, and D2C mobile apps — engineered for the speed, personalisation, and reliability demands of modern retail.
            </p>

            {/* Rotating metric pill */}
            <div style={{ background: "#FFF1F2", border: "1px solid #FECDD3", borderRadius: 12, padding: "13px 20px", marginBottom: 36, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14.5, color: "#18181B" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 48 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#18181B,#E11D48)", color: "#fff",
                padding: "15px 32px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 8px 28px rgba(225,29,72,.35)",
                transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(225,29,72,.48)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(225,29,72,.35)"; }}
              >🛒 Start Commerce Project</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#18181B",
                padding: "15px 30px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "2px solid #FECDD3",
                transition: "all .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#FFF1F2"; e.currentTarget.style.borderColor = "#E11D48"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#FECDD3"; }}
              >📂 View Case Studies</a>
            </div>

            {/* Trust signal row */}
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center" }}>
              {[["200+", "Commerce Projects"], ["₹800Cr+", "GMV Processed"], ["2.4×", "Avg Conversion ↑"], ["99.9%", "Checkout Uptime"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#E11D48" }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: "#9CA3AF", fontWeight: 600, marginTop: 2, fontFamily: "'Sora',sans-serif" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual panel */}
          <div style={{ position: "relative" }}>
            {/* Main image card */}
            <div style={{
              borderRadius: 28, overflow: "hidden",
              boxShadow: "0 40px 100px rgba(225,29,72,.14), 0 0 0 1px rgba(225,29,72,.08)",
              background: "#fff", position: "relative",
            }}>
              {/* Dashboard chrome bar */}
              <div style={{ background: "#FFF5F6", borderBottom: "1px solid #FECDD3", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444", "#F59E0B", "#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "#FFF1F2", borderRadius: 8, padding: "5px 14px", fontSize: 12, color: "#E11D48", fontWeight: 600, margin: "0 12px", fontFamily: "'Sora',sans-serif" }}>
                  🔒 stylenest-store.techvanto.com
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, fontFamily: "'Sora',sans-serif", background: "#FFF1F2", padding: "4px 10px", borderRadius: 99, border: "1px solid #FECDD3" }}>LIVE</div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=640&h=380&fit=crop&auto=format"
                alt="E-Commerce Platform"
                style={{ width: "100%", height: 340, objectFit: "cover", display: "block" }}
              />
              {/* Overlay gradient */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 100, background: "linear-gradient(to top,rgba(24,24,27,.3),transparent)" }} />
            </div>

            {/* Floating badge — GMV */}
            <div style={{ position: "absolute", top: -18, left: -22, background: "#fff", borderRadius: 18, padding: "14px 18px", boxShadow: "0 12px 40px rgba(225,29,72,.18)", border: "1.5px solid #FECDD3", animation: "float 3.2s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: "linear-gradient(135deg,#18181B,#E11D48)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>💰</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14, color: "#18181B" }}>₹800Cr+</div>
                  <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>Annual GMV Processed</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Conversion */}
            <div style={{ position: "absolute", bottom: 20, right: -22, background: "linear-gradient(135deg,#18181B,#E11D48)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 12px 36px rgba(225,29,72,.4)", animation: "float 3.8s ease-in-out infinite .6s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#fff" }}>2.4×</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Avg Conversion Lift 📈</div>
            </div>

            {/* Floating badge — AI */}
            <div style={{ position: "absolute", bottom: -18, left: 16, background: "#fff", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.09)", border: "1.5px solid #EDE9FE", animation: "float 4.2s ease-in-out infinite 1.2s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 20 }}>🤖</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0F172A", fontFamily: "'Sora',sans-serif" }}>AI Recommendations</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>+28% avg order value</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Channels */}
            <div style={{ position: "absolute", top: 56, right: -20, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 6px 22px rgba(124,58,237,.12)", border: "1.5px solid #EDE9FE", animation: "float 4.6s ease-in-out infinite 1.8s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>🔗</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#5B21B6", fontFamily: "'Sora',sans-serif" }}>ONDC + 8 Channels</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>Omnichannel Ready</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#18181B", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }} className="ec-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>{s.icon}</div>
            <div style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(28px,3vw,44px)", fontWeight: 900, color: "#fff",
              lineHeight: 1,
            }}>{s.val}</div>
            <div style={{ color: "#FDA4AF", fontSize: 13, marginTop: 8, fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "100px 40px", background: "#fff" }} className="ec-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#E11D48", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#18181B", marginTop: 10, marginBottom: 16 }}>Retail & E-Commerce Solutions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 580, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>
            Conversion-first commerce technology built for the speed and scale of modern retail — from a D2C startup to a pan-India multi-channel retailer with 1M+ monthly orders.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "34px 30px",
                border: `2px solid ${hov === i ? s.color : "#FFF1F2"}`,
                boxShadow: hov === i ? `0 28px 72px ${s.color}1E` : "0 2px 16px rgba(0,0,0,.04)",
                transition: "all .32s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-8px)" : "none",
                position: "relative", overflow: "hidden", cursor: "pointer",
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${s.color},${s.color}55)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${s.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${s.color}20` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#18181B", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, marginBottom: 20, fontFamily: "'Sora',sans-serif" }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}0E`, color: s.color, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, border: `1px solid ${s.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .22s", fontFamily: "'Sora',sans-serif" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FFF5F6" }} className="ec-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#E11D48", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#18181B", marginTop: 10, marginBottom: 16 }}>Commerce Depth Meets Engineering Excellence</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 540, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>What separates a generic IT company from a true retail technology partner.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {WHY_US.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#FECDD3"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}16` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 54, height: 54, borderRadius: 16, background: `${p.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1.5px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#18181B", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, fontFamily: "'Sora',sans-serif" }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "100px 40px", background: "#fff" }} className="ec-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Technology</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#18181B", marginTop: 10, marginBottom: 16 }}>Commerce Technology Stack</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>The frameworks, tools, and platforms powering our retail solutions — chosen for performance, SEO, and conversion impact.</p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#18181B,#E11D48)" : "#fff",
              borderColor: active === i ? "#E11D48" : "#FECDD3",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(225,29,72,.28)" : "none",
            }}>{t.category}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 22,
                border: `2px solid ${hov === i ? tech.color : "#FFF1F2"}`,
                background: hov === i ? `${tech.color}07` : "#FFF5F6",
                boxShadow: hov === i ? `0 14px 42px ${tech.color}22` : "0 1px 6px rgba(0,0,0,.04)",
                transition: "all .3s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-7px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}14` : "#fff", border: `1px solid ${hov === i ? tech.color + "28" : "#FECDD3"}`, boxShadow: hov === i ? `0 4px 18px ${tech.color}20` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .3s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .3s", transform: hov === i ? "scale(1.12)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 3, fontFamily: "'Sora',sans-serif" }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 20px 20px", background: `linear-gradient(90deg,${tech.color},${tech.color}60)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "100px 40px", background: "linear-gradient(155deg,#FFF1F2 0%,#F5F3FF 50%,#EFF6FF 100%)" }} className="ec-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 76 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#18181B", marginTop: 10, marginBottom: 16 }}>How We Build Commerce Software</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>A conversion-first development process — designed around shopper behaviour, merchant operations, and peak-traffic reliability.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 24, padding: "34px 30px",
              border: "1.5px solid #FECDD3",
              boxShadow: "0 4px 20px rgba(225,29,72,.06)",
              position: "relative", overflow: "hidden",
              transition: "all .32s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = step.color; e.currentTarget.style.boxShadow = `0 20px 56px ${step.color}16`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#FECDD3"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(225,29,72,.06)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}10`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${step.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${step.color}22` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}12`, padding: "3px 12px", borderRadius: 99, display: "inline-block", marginBottom: 12, letterSpacing: ".04em", fontFamily: "'Sora',sans-serif" }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#18181B", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}44)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "100px 40px", background: "#fff" }} className="ec-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#DC2626", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#18181B", marginTop: 10, marginBottom: 16 }}>Commerce Platforms We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>Production e-commerce platforms processing real orders for real brands — with the GMV and conversion metrics that matter in retail.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 68 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 56, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="ec-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}1E` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 380, objectFit: "cover", display: "block", transition: "transform .5s ease", transform: hov === cs.title ? "scale(1.05)" : "scale(1)" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${cs.color}44,transparent 60%)` }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 16px", borderRadius: 99, fontFamily: "'Sora',sans-serif" }}>{cs.industry}</div>
              </div>

              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,40px)", fontWeight: 900, color: "#18181B", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.85, marginBottom: 26, fontFamily: "'Sora',sans-serif" }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 30 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}0E`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "5px 13px", borderRadius: 99, border: `1px solid ${cs.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 32, marginBottom: 36, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 28, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 5, fontFamily: "'Sora',sans-serif" }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}30`,
                  transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${cs.color}44`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 6px 20px ${cs.color}30`; }}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 68 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#E11D48", border: "2px solid #FECDD3",
            padding: "14px 36px", borderRadius: 12, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .22s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#FFF1F2"; e.currentTarget.style.borderColor = "#E11D48"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#FECDD3"; }}
          >View All Commerce Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FFF5F6" }} className="ec-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#18181B", marginTop: 10, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.7, fontFamily: "'Sora',sans-serif" }}>Everything you need to know before starting your retail and e-commerce technology project.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 18,
              border: `2px solid ${open === i ? "#E11D48" : "#FECDD3"}`,
              overflow: "hidden", transition: "border-color .25s, box-shadow .25s",
              boxShadow: open === i ? "0 8px 36px rgba(225,29,72,.1)" : "0 1px 4px rgba(0,0,0,.04)",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 30px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 600, fontSize: 15.5, color: "#18181B", textAlign: "left", lineHeight: 1.45, flex: 1, marginRight: 18 }}>{faq.q}</span>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: open === i ? "linear-gradient(135deg,#18181B,#E11D48)" : "#FFF1F2",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .28s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#E11D48", fontSize: 19, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .28s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 30px 26px", animation: "fadeIn .22s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.85, borderTop: "1px solid #FFF1F2", paddingTop: 18, fontFamily: "'Sora',sans-serif" }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function RetailEcommerceSolution() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.4; } }
        @keyframes ecPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.45; transform:scale(0.96); } }

        /* ── Desktop base ── */
        .nav-links       { display:flex; align-items:center; gap:4px; }
        .nav-cta         { display:flex !important; }
        .mob-menu-btn    { display:none; }

        .ec-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .ec-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; text-align:center; }
        .ec-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .footer-grid     { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }

        /* ── ≤1100px ── */
        @media (max-width:1100px) {
          .ec-hero-grid  { grid-template-columns:1fr 1fr; gap:40px; }
          .footer-grid   { grid-template-columns:1fr 1fr; gap:32px; }
          .ec-stats-grid { grid-template-columns:repeat(2,1fr); }
        }

        /* ── ≤900px ── */
        @media (max-width:900px) {
          .ec-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .ec-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links       { display:none !important; }
          .nav-cta         { display:none !important; }
          .mob-menu-btn    { display:flex !important; }
          .ec-section      { padding-left:20px !important; padding-right:20px !important; }
        }

        /* ── ≤640px ── */
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .ec-stats-grid   { grid-template-columns:repeat(2,1fr); gap:24px; }
          .ec-section      { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; }
          section          { padding-left:16px !important; padding-right:16px !important; }
          footer           { padding-left:16px !important; padding-right:16px !important; }
        }

        /* ── ≤480px ── */
        @media (max-width:480px) {
          .footer-grid     { grid-template-columns:1fr !important; gap:28px; }
          .ec-stats-grid   { grid-template-columns:1fr 1fr; gap:20px; }
        }

        /* ── ≤500px ── */
        @media (max-width:500px) {
          .topbar-right    { display:none !important; }
        }

        /* ── Navbar padding on mobile ── */
        @media (max-width:640px) {
          nav > div:nth-child(2) { padding: 0 16px !important; }
          nav > div:first-child  { padding: 6px 16px !important; }
        }

        /* ── Hero floating badges — prevent overflow on mobile ── */
        @media (max-width:640px) {
          .ec-hero-grid > div:last-child { margin-top: 8px; }
        }
      `}</style>

      <Navbar />
      <Hero />
      <StatsBar />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}